import { calculateBlendedPrice, getVolumeDiscount, calculateTradePrice } from '../../../Utils/HelperFunctions';

/**
 * Calculate price for a specific trade with inflation applied
 * @param {Object} trade - The trade object
 * @param {Set} selectedScopeIds - Selected scope IDs for the trade
 * @param {number} inflationPercentage - Inflation percentage to apply
 * @returns {number} The calculated trade price
 */
export const calculateTradePriceForTradeHelper = (trade, selectedScopeIds, inflationPercentage) => {
  if (!trade) return 0;

  const basePrice = calculateTradePrice(trade, selectedScopeIds);

  // Apply inflation if set
  if (inflationPercentage > 0) {
    return basePrice * (1 + inflationPercentage / 100);
  }

  return basePrice;
};

/**
 * Calculate comprehensive pricing for all selected trades (without manual discount)
 * Manual discount should be applied separately after this calculation
 * @param {Object} params - Pricing calculation parameters
 * @returns {Object} Pricing breakdown object
 */
export const calculatePricingHelper = ({
  selectedTrades,
  tradesData,
  selectedScopes,
  inflationPercentage,
  perTradeDistribution,
  totalAnnualSheets,
  volumeDiscountTiers
}) => {
  const tradesDataForPricing = Array.from(selectedTrades).map(tradeId => {
    const trade = tradesData.find(t => t.trade_id === tradeId);
    const selectedScopeIds = Array.from(selectedScopes[tradeId] || []);

    return {
      trade_id: tradeId,
      trade_note: trade?.trade_description || '',
      sheet_count: perTradeDistribution[tradeId] || Math.floor(totalAnnualSheets / selectedTrades.size),
      scope_ids: selectedScopeIds,
      trade_cost: calculateTradePriceForTradeHelper(trade, selectedScopeIds, inflationPercentage)
    };
  });

  const totalSheetsFromTrades = tradesDataForPricing.reduce((sum, trade) => sum + trade.sheet_count, 0);

  const blendedPrice = calculateBlendedPrice(tradesDataForPricing, totalSheetsFromTrades);
  const volumeDiscount = getVolumeDiscount(totalSheetsFromTrades, volumeDiscountTiers);

  const priceAfterVolumeDiscount = blendedPrice * (1 - volumeDiscount);

  return {
    tradesData: tradesDataForPricing,
    blendedPrice,
    volumeDiscount,
    priceAfterVolumeDiscount,
    annualCostAfterVolumeDiscount: priceAfterVolumeDiscount * totalSheetsFromTrades,
    totalSheetsFromTrades
  };
};
